import { useCallback, useEffect, useState, useMemo } from "react";
import {
	getClarificationsBySubmissionId,
	getClarificationsByRfxId,
	getClarificationTypes,
	createClarification,
	updateClarification,
	deleteClarification,
	sendClarifications,
	exportClarifications,
	downloadImportTemplate,
	importClarifications,
	validateImportClarifications,
	saveDraftResponse as saveDraftResponseApi,
	sendResponses as sendResponsesApi,
} from "../api/clarification-api";
import {
	Clarification,
	ClarificationType,
	CreateClarificationPayload,
	UpdateClarificationPayload,
	ImportClarificationsResult,
	ValidateImportResult,
	ImportClarificationRow,
	SubmitClarificationResponsePayload,
	ClarificationStatus,
} from "../types/clarification.type";

interface UseClarificationsOptions {
	isClientView?: boolean;
	rfxId?: number;
	rfxSubmissionId?: number;
}

interface UseClarificationsReturn {
	clarifications: Clarification[];
	clarificationsReadyToSend: Clarification[];
	clarificationsWithDraftResponses: Clarification[];
	clarificationTypes: ClarificationType[];
	draftClarifications: Clarification[];
	isLoading: boolean;
	sentClarifications: Clarification[];
	addClarification: (payload: CreateClarificationPayload) => Promise<boolean>;
	editClarification: (
		id: number,
		payload: UpdateClarificationPayload
	) => Promise<boolean>;
	handleDownloadTemplate: () => Promise<void>;
	handleExport: () => Promise<void>;
	handleImport: (
		rows: ImportClarificationRow[]
	) => Promise<ImportClarificationsResult | null>;
	handleValidateImport: (file: File) => Promise<ValidateImportResult | null>;
	refetch: () => Promise<void>;
	removeClarification: (id: number) => Promise<boolean>;
	saveDraftResponse: (
		id: number,
		payload: SubmitClarificationResponsePayload
	) => Promise<boolean>;
	sendDraftClarifications: (ids: number[], dateDue: string) => Promise<boolean>;
	sendSelectedResponses: (ids: number[]) => Promise<boolean>;
	updateLocalClarifications: (clarifications: Clarification[]) => void;
}

const getStatusSortOrder = (status: ClarificationStatus) => {
	switch (status) {
		case ClarificationStatus.open:
			return 1;
		case ClarificationStatus.pending:
			return 2;
		case ClarificationStatus.closed:
			return 3;
		case ClarificationStatus.draft:
			return 4;
	}
};

export const useClarifications = ({
	rfxId,
	rfxSubmissionId,
	isClientView = false,
}: UseClarificationsOptions): UseClarificationsReturn => {
	const [clarifications, setClarifications] = useState<Clarification[]>([]);
	const [clarificationTypes, setClarificationTypes] = useState<
		ClarificationType[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);

	const updateLocalClarifications = (clarifications: Clarification[]) => {
		setClarifications(clarifications);
	};

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			const [data, types] = await Promise.all([
				rfxSubmissionId
					? getClarificationsBySubmissionId(rfxSubmissionId)
					: rfxId
						? getClarificationsByRfxId(rfxId)
						: [],
				getClarificationTypes(),
			]);

			// Filter to approved clarifications only — panel member draft
			// clarifications (is_approved=false) are scoped to the evaluation
			// step and should not appear on the main Clarifications Tab.
			const approvedData = data.filter(
				(c) => c.is_approved === undefined || c.is_approved !== false
			);

			const sortedData = approvedData.sort(
				(a, b) => getStatusSortOrder(a.status) - getStatusSortOrder(b.status)
			);

			setClarifications(sortedData);
			setClarificationTypes(types);
		} finally {
			setIsLoading(false);
		}
	}, [rfxId, rfxSubmissionId]);

	useEffect(() => {
		if (rfxId || rfxSubmissionId) {
			fetchData();
		}
	}, [fetchData, rfxId, rfxSubmissionId]);

	// Separate draft (unsent) and sent clarifications
	const draftClarifications = clarifications.filter((c) => !c.is_sent);
	const sentClarifications = clarifications.filter((c) => c.is_sent);

	// Clarifications where supplier has drafted but not sent response
	const clarificationsWithDraftResponses = useMemo(() => {
		return clarifications.filter(
			(c) => c.is_draft_response && c.final_response
		);
	}, [clarifications]);

	// Alias for clarity in UI
	const clarificationsReadyToSend = clarificationsWithDraftResponses;

	const addClarification = useCallback(
		async (payload: CreateClarificationPayload): Promise<boolean> => {
			if (!rfxSubmissionId) {
				return false;
			}
			const result = await createClarification(rfxSubmissionId, payload);
			if (result) {
				setClarifications((prev) => [result, ...prev]);
				return true;
			}
			return false;
		},
		[rfxSubmissionId]
	);

	const editClarification = useCallback(
		async (
			id: number,
			payload: UpdateClarificationPayload
		): Promise<boolean> => {
			const result = await updateClarification(id, payload);
			if (result) {
				setClarifications((prev) =>
					prev.map((c) => (c.id === id ? result : c))
				);
				return true;
			}
			return false;
		},
		[]
	);

	const removeClarification = useCallback(
		async (id: number): Promise<boolean> => {
			const result = await deleteClarification(id);
			if (result) {
				setClarifications((prev) => prev.filter((c) => c.id !== id));
				return true;
			}
			return false;
		},
		[]
	);

	const sendDraftClarifications = useCallback(
		async (ids: number[], dateDue: string): Promise<boolean> => {
			if (!rfxSubmissionId) {
				return false;
			}
			const result = await sendClarifications(rfxSubmissionId, {
				clarification_ids: ids,
				date_due: dateDue,
			});
			if (result) {
				// update conversations due date, status and sent indicator
				setClarifications((prev) =>
					prev.map((currentConversation) => {
						const isUpdated = ids.some((val) => val === currentConversation.id);

						return isUpdated
							? ({
									...currentConversation,
									is_sent: true,
									date_due: dateDue,
									status: ClarificationStatus.open,
								} as Clarification)
							: currentConversation;
					})
				);
				return true;
			}
			return false;
		},
		[rfxSubmissionId]
	);

	const handleExport = useCallback(async () => {
		if (!rfxSubmissionId) {
			return;
		}
		const result = await exportClarifications(rfxSubmissionId);

		if (!result || !result.blob) {
			return;
		}

		const url = window.URL.createObjectURL(result.blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = result.filename ?? `clarifications-${rfxSubmissionId}.xlsx`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	}, [rfxSubmissionId]);

	const handleDownloadTemplate = useCallback(async () => {
		const blob = await downloadImportTemplate();
		if (blob) {
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "clarifications-import-template.xlsx";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		}
	}, []);

	const handleValidateImport = useCallback(
		async (file: File): Promise<ValidateImportResult | null> => {
			if (!rfxSubmissionId) {
				return null;
			}
			return validateImportClarifications(rfxSubmissionId, file);
		},
		[rfxSubmissionId]
	);

	const handleImport = useCallback(
		async (
			rows: ImportClarificationRow[]
		): Promise<ImportClarificationsResult | null> => {
			if (!rfxSubmissionId) {
				return null;
			}
			const result = await importClarifications(rfxSubmissionId, rows);
			if (result && result.success) {
				// Refetch to get the newly imported clarifications
				await fetchData();
			}
			return result;
		},
		[rfxSubmissionId, fetchData]
	);

	const saveDraftResponse = useCallback(
		async (
			clarificationId: number,
			payload: SubmitClarificationResponsePayload
		): Promise<boolean> => {
			const result = await saveDraftResponseApi(clarificationId, payload);
			if (result) {
				setClarifications((prev) =>
					prev.map((c) => (c.id === clarificationId ? result : c))
				);
				return true;
			}
			return false;
		},
		[]
	);

	const sendSelectedResponses = useCallback(
		async (clarificationIds: number[]): Promise<boolean> => {
			const result = await sendResponsesApi({
				clarification_ids: clarificationIds,
			});
			if (result) {
				setClarifications((prev) =>
					prev.map((c) => {
						const updatedItem = result.find((u) => u.id === c.id);
						return updatedItem || c;
					})
				);
				return true;
			}
			return false;
		},
		[]
	);

	return {
		clarifications,
		clarificationTypes,
		draftClarifications,
		sentClarifications,
		clarificationsWithDraftResponses,
		clarificationsReadyToSend,
		isLoading,
		refetch: fetchData,
		addClarification,
		editClarification,
		removeClarification,
		sendDraftClarifications,
		handleExport,
		handleDownloadTemplate,
		handleImport,
		handleValidateImport,
		saveDraftResponse,
		sendSelectedResponses,
		updateLocalClarifications,
	};
};
